/** @jsxImportSource @emotion/react */
import type { OverallTaskStatus } from '@kaiizen/kai-pro-api';

import { DEFAULT_TASK_STATUS_BADGE_STYLES, TaskStatusBadgeStyles } from './taskStatusBadgeStyles';

/**
 * Content, not style — kept out of `taskStatusBadgeStyles.ts` and here instead, the same
 * way the previous widget kept `OVERALL_TASK_STATUS_LABEL` beside its component rather
 * than in its (nonexistent) styles module.
 *
 * "Rework", not "Needs rework" — the prototype's own wording (`Kai_ShopEdit_Prototype.html`'s
 * STATUS map), now this widget's one and only label for the state.
 */
export const TASK_STATUS_BADGE_LABEL: Record<OverallTaskStatus, string> = {
  empty: 'Empty',
  draft: 'Draft',
  approved: 'Approved',
  notStarted: 'Not started',
  inProgress: 'In progress',
  needsRework: 'Rework',
  completed: 'Completed',
};

/**
 * THE task-status badge — one widget for every surface that shows a task's status:
 * the Kai Navigator's rows, the editor's task tables, the task header's chip.
 *
 * Styling comes from the Kai Styleguide's "Status & badges" section (the one
 * source with authority over every mockup), re-drawn per the ShopEdit prototype's
 * own task-status pill: Maxeville Construct over the paired palette fills, kai-shiki's
 * work-status icon leading the label for the four states the work ladder actually
 * walks through.
 */
export const TaskStatusBadge = ({
  status,
  iconOnly = false,
  styles = DEFAULT_TASK_STATUS_BADGE_STYLES,
}: {
  /** Resolved once in the framework — see `ScanEditingContext.overallTaskStatusByTaskId`. */
  status: OverallTaskStatus;
  /** Drops the chip and its label text down to just the marker, carrying the label as a native tooltip instead (`title`/`aria-label`)
   *  for dense table rows where the label would otherwise repeat down a whole column. */
  iconOnly?: boolean;
  styles?: TaskStatusBadgeStyles;
}) => {
  const { tone, Icon, default: showDefaultMarker } = styles.statusStyles[status];
  const label = TASK_STATUS_BADGE_LABEL[status];

  const marker = (iconSize: number, defaultMarkerStyle: TaskStatusBadgeStyles['defaultMarker']) =>
    Icon
      ? <Icon size={iconSize} color={tone.color} />
      : showDefaultMarker
      ? <span css={defaultMarkerStyle} />
      : null;

  if (iconOnly) {
    return (
      <span
        css={styles.iconOnlyContainer}
        style={{ color: tone.color, border: tone.border }}
        data-testid={'task-status-badge'}
        data-status={status}
        title={label}
        aria-label={label}
        role={'img'}
      >
        {marker(styles.iconOnlyIconSize, styles.iconOnlyDefaultMarker)}
      </span>
    );
  }

  return (
    <span
      css={styles.base}
      style={tone}
      data-testid={'task-status-badge'}
      data-status={status}
    >
      {marker(styles.iconSize, styles.defaultMarker)}
      {label}
    </span>
  );
};
